import React, {useEffect, useMemo, useRef, useState} from "react";
import {Modal, ModalBody, ModalHeader} from "reactstrap";
import {ItemQuantity} from "../State/Inventory";
import {
    SmeltingUnlockModalProps,
    TemperatureControlGameProps,
    MetalPouringGameProps,
    BrickInspectionGameProps,
    TEMPERATURE_RESOURCES,
    POURING_RESOURCES,
    INSPECTION_RESOURCES,
    getTemperatureSettings,
    getPouringSettings,
    getInspectionSettings,
    MoldState,
    BrickState,
} from "./smelterUnlockLogic";
import {
    TemperatureGameContainer,
    TemperatureGauge,
    TemperatureNeedle,
    AirflowBar,
    AirflowFill,
    WheelContainer,
    Wheel,
    StatusText,
    PouringGameContainer,
    Crucible,
    MoldConveyor,
    Mold,
    MoldFill,
    TargetLine,
    ToleranceZone,
    ProgressCounter,
    InspectionGameContainer,
    BrickGrid,
    Brick,
    BrickCountDisplay,
    GameWrapper,
} from "./SmeltingUnlockStyles";

export function SmeltingUnlockModal({resourceType, clearedSpots, isOpen, onClose, onComplete}: SmeltingUnlockModalProps) {
    const isTemperatureGame = TEMPERATURE_RESOURCES.includes(resourceType);
    const isPouringGame = POURING_RESOURCES.includes(resourceType);
    const isInspectionGame = INSPECTION_RESOURCES.includes(resourceType);

    const gameTitle = isTemperatureGame
        ? "Regulate Furnace Temperature"
        : isPouringGame
        ? "Pour Molten Steel"
        : "Inspect Stone Bricks";

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg" centered>
            <ModalHeader toggle={onClose}>Prepare Furnace Foundation - {gameTitle}</ModalHeader>
            <ModalBody>
                {isTemperatureGame && (
                    <TemperatureControlGame
                        resourceType={resourceType}
                        clearedSpots={clearedSpots}
                        onComplete={(rewards) => {
                            onComplete(rewards);
                            onClose();
                        }}
                    />
                )}
                {isPouringGame && (
                    <MetalPouringGame
                        resourceType={resourceType}
                        clearedSpots={clearedSpots}
                        onComplete={(rewards) => {
                            onComplete(rewards);
                            onClose();
                        }}
                    />
                )}
                {isInspectionGame && (
                    <BrickInspectionGame
                        resourceType={resourceType}
                        clearedSpots={clearedSpots}
                        onComplete={(rewards) => {
                            onComplete(rewards);
                            onClose();
                        }}
                    />
                )}
            </ModalBody>
        </Modal>
    );
}

function TemperatureControlGame({resourceType, clearedSpots, onComplete}: TemperatureControlGameProps) {
    const settings = useMemo(() => getTemperatureSettings(clearedSpots), [clearedSpots]);
    const [temperature, setTemperature] = useState(20);
    const [airflow, setAirflow] = useState(0);
    const [wheelRotation, setWheelRotation] = useState(0);
    const [timeInZone, setTimeInZone] = useState(0);
    const [timeOutOfZone, setTimeOutOfZone] = useState(0);
    const [gameStarted, setGameStarted] = useState(false);
    const [success, setSuccess] = useState(false);
    const [failure, setFailure] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [lastMouseAngle, setLastMouseAngle] = useState(0);
    const wheelRef = useRef<HTMLDivElement>(null);
    const temperatureRef = useRef(20);

    // Temperature simulation with smooth real-time updates
    useEffect(() => {
        if (success || failure) return;

        let animationFrameId: number;
        let lastTime = performance.now();

        const updateTemperature = (currentTime: number) => {
            const deltaTime = currentTime - lastTime;
            lastTime = currentTime;

            setTemperature(prev => {
                const targetTemp = airflow * 0.8; // Airflow determines target temperature
                const diff = targetTemp - prev;
                // Normalize the change rate to be consistent regardless of frame rate
                const changeRate = 0.15 * (deltaTime / 100);
                const newTemp = prev + diff * changeRate;
                temperatureRef.current = newTemp;
                return newTemp;
            });

            animationFrameId = requestAnimationFrame(updateTemperature);
        };

        animationFrameId = requestAnimationFrame(updateTemperature);

        return () => cancelAnimationFrame(animationFrameId);
    }, [airflow, success, failure]);

    // Airflow decay
    useEffect(() => {
        if (success || failure) return;

        const interval = setInterval(() => {
            setAirflow(prev => Math.max(0, prev - 0.5));
        }, 100);

        return () => clearInterval(interval);
    }, [success, failure]);

    // Timer for green zone
    useEffect(() => {
        if (success || failure) return;

        const interval = setInterval(() => {
            const currentTemp = temperatureRef.current;
            const currentInZone = currentTemp >= settings.greenZoneMin && currentTemp <= settings.greenZoneMax;

            setTimeInZone(prev => {
                if (currentInZone) {
                    const newTime = prev + 100;
                    if (newTime >= settings.maintainTime * 1000) {
                        setSuccess(true);
                        setTimeout(() => onComplete([]), 1000);
                    }
                    setTimeOutOfZone(0);
                    return newTime;
                }
                return prev;
            });

            setTimeOutOfZone(prev => {
                if (!currentInZone && gameStarted && currentTemp > settings.greenZoneMin - 5) {
                    const newTime = prev + 100;
                    if (newTime >= settings.penaltyTime * 1000) {
                        setFailure(true);
                    }
                    return newTime;
                }
                return 0;
            });
        }, 100);

        return () => clearInterval(interval);
    }, [gameStarted, settings, onComplete, success, failure]);

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setGameStarted(true);
        const rect = wheelRef.current?.getBoundingClientRect();
        if (rect) {
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);
            setLastMouseAngle(angle);
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !wheelRef.current) return;

        const rect = wheelRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);

        let deltaAngle = angle - lastMouseAngle;
        if (deltaAngle > Math.PI) deltaAngle -= 2 * Math.PI;
        if (deltaAngle < -Math.PI) deltaAngle += 2 * Math.PI;

        const rotationDelta = (deltaAngle * 180) / Math.PI;
        setWheelRotation(prev => prev + rotationDelta);

        // Add airflow based on rotation speed
        const airflowIncrease = Math.abs(rotationDelta) / (settings.wheelSpinsRequired * 3.6);
        setAirflow(prev => Math.min(100, prev + airflowIncrease));

        setLastMouseAngle(angle);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const needleAngle = -90 + (temperature / 100) * 180;
    const timeRemaining = Math.max(0, settings.maintainTime - timeInZone / 1000);
    const inGreenZone = temperature >= settings.greenZoneMin && temperature <= settings.greenZoneMax;

    return (
        <TemperatureGameContainer>
            <p>Spin the wheel to add air and raise the temperature. Keep it in the green zone for {settings.maintainTime} seconds!</p>
            <TemperatureGauge $greenMin={settings.greenZoneMin} $greenMax={settings.greenZoneMax}>
                <TemperatureNeedle $angle={needleAngle} />
            </TemperatureGauge>
            <div>Airflow:</div>
            <AirflowBar>
                <AirflowFill $percentage={airflow} />
            </AirflowBar>
            <WheelContainer
                ref={wheelRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <Wheel $rotation={wheelRotation} />
            </WheelContainer>
            <StatusText $success={success} $failure={failure}>
                {success && "Success! Foundation prepared!"}
                {failure && "Failed! Temperature control lost."}
                {!success && !failure && inGreenZone && `Hold steady! ${timeRemaining.toFixed(1)}s remaining`}
                {!success && !failure && !inGreenZone && gameStarted && "Get into the green zone!"}
                {!success && !failure && !gameStarted && "Spin the wheel to start!"}
            </StatusText>
        </TemperatureGameContainer>
    );
}

function MetalPouringGame({resourceType, clearedSpots, onComplete}: MetalPouringGameProps) {
    const settings = useMemo(() => getPouringSettings(clearedSpots), [clearedSpots]);
    const [currentMold, setCurrentMold] = useState(0);
    const [molds, setMolds] = useState<MoldState[]>([]);
    const [pouring, setPouring] = useState(false);
    const [success, setSuccess] = useState(false);

    // Initialize first mold
    useEffect(() => {
        setMolds([{
            id: 0,
            fullness: 0,
            targetFullness: settings.targetFullness,
            pouring: false,
            complete: false,
            failed: false,
        }]);
    }, [settings.targetFullness]);

    // Pouring logic
    useEffect(() => {
        if (!pouring || molds.length === 0) return;

        const interval = setInterval(() => {
            setMolds(prev => prev.map((mold, idx) => {
                if (idx !== currentMold) return mold;
                return {
                    ...mold,
                    fullness: Math.min(100, mold.fullness + settings.fillSpeed),
                    pouring: true,
                };
            }));
        }, 100);

        return () => clearInterval(interval);
    }, [pouring, currentMold, molds.length, settings.fillSpeed]);

    const handleMouseDown = () => {
        if (success || molds.length === 0) return;
        const mold = molds[currentMold];
        if (!mold || mold.complete || mold.failed) return;
        setPouring(true);
    };

    const handleMouseUp = () => {
        setPouring(false);
        const mold = molds[currentMold];
        if (!mold || mold.complete || mold.failed) return;

        const error = Math.abs(mold.fullness - mold.targetFullness);
        const isCorrect = error <= settings.tolerance;

        setMolds(prev => prev.map((m, idx) => {
            if (idx !== currentMold) return m;
            return {
                ...m,
                complete: isCorrect,
                failed: !isCorrect,
                pouring: false,
            };
        }));

        if (isCorrect) {
            const correctCount = molds.filter(m => m.complete).length + 1;
            if (correctCount >= settings.moldsRequired) {
                setSuccess(true);
                setTimeout(() => onComplete([]), 1000);
            } else {
                setTimeout(() => {
                    setCurrentMold(prev => prev + 1);
                    setMolds(prev => [...prev, {
                        id: prev.length,
                        fullness: 0,
                        targetFullness: settings.targetFullness,
                        pouring: false,
                        complete: false,
                        failed: false,
                    }]);
                }, 500);
            }
        } else {
            setTimeout(() => {
                setMolds(prev => prev.map((m, idx) => {
                    if (idx !== currentMold) return m;
                    return {
                        ...m,
                        fullness: 0,
                        failed: false,
                    };
                }));
            }, 1000);
        }
    };

    const correctCount = molds.filter(m => m.complete).length;
    const activeMold = molds[currentMold];

    return (
        <PouringGameContainer>
            <p>Click and hold to pour molten steel. Release at the green line!</p>
            <Crucible $pouring={pouring} />
            <MoldConveyor>
                {activeMold && (
                    <Mold
                        $fullness={activeMold.fullness}
                        $target={activeMold.targetFullness}
                        $complete={activeMold.complete}
                        $failed={activeMold.failed}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    >
                        <ToleranceZone $target={settings.targetFullness} $tolerance={settings.tolerance} />
                        <TargetLine $target={settings.targetFullness} />
                        <MoldFill $fullness={activeMold.fullness} />
                    </Mold>
                )}
            </MoldConveyor>
            <ProgressCounter>
                {correctCount} / {settings.moldsRequired} molds complete
            </ProgressCounter>
            <StatusText $success={success}>
                {success && "Success! All molds filled correctly!"}
                {!success && activeMold?.failed && "Try again! Pour more accurately."}
                {!success && !activeMold?.failed && "Click and hold to pour!"}
            </StatusText>
        </PouringGameContainer>
    );
}

function BrickInspectionGame({resourceType, clearedSpots, onComplete}: BrickInspectionGameProps) {
    const settings = useMemo(() => getInspectionSettings(clearedSpots), [clearedSpots]);
    const [bricks, setBricks] = useState<BrickState[]>([]);
    const [clickedCount, setClickedCount] = useState(0);
    const [success, setSuccess] = useState(false);

    // Initialize bricks
    useEffect(() => {
        const newBricks: BrickState[] = [];
        const brokenIndices = new Set<number>();

        // Select random broken bricks
        while (brokenIndices.size < settings.brokenCount) {
            brokenIndices.add(Math.floor(Math.random() * settings.gridSize * settings.gridSize));
        }

        for (let row = 0; row < settings.gridSize; row++) {
            for (let col = 0; col < settings.gridSize; col++) {
                const id = row * settings.gridSize + col;
                newBricks.push({
                    id,
                    row,
                    col,
                    isBroken: brokenIndices.has(id),
                    isClicked: false,
                });
            }
        }

        setBricks(newBricks);
    }, [settings.gridSize, settings.brokenCount]);

    const handleBrickClick = (brickId: number) => {
        if (success) return;

        const brick = bricks.find(b => b.id === brickId);
        if (!brick || brick.isClicked) return;

        if (brick.isBroken) {
            setBricks(prev => prev.map(b =>
                b.id === brickId ? {...b, isClicked: true} : b
            ));
            const newClickedCount = clickedCount + 1;
            setClickedCount(newClickedCount);

            if (newClickedCount >= settings.brokenCount) {
                setSuccess(true);
                setTimeout(() => onComplete([]), 1000);
            }
        }
    };

    return (
        <InspectionGameContainer>
            <p>Inspect bricks by hovering over them. Click all {settings.brokenCount} broken bricks marked with a cross!</p>
            <BrickCountDisplay>
                {clickedCount} / {settings.brokenCount} broken bricks found
            </BrickCountDisplay>
            <BrickGrid $gridSize={settings.gridSize}>
                {bricks.map(brick => (
                    <Brick
                        key={brick.id}
                        $broken={brick.isBroken}
                        $clicked={brick.isClicked}
                        $row={brick.row}
                        $gridSize={settings.gridSize}
                        onClick={() => handleBrickClick(brick.id)}
                    />
                ))}
            </BrickGrid>
            <StatusText $success={success}>
                {success && "Success! All broken bricks found!"}
                {!success && "Hover over bricks to inspect them!"}
            </StatusText>
        </InspectionGameContainer>
    );
}
